import React, { useState, useEffect } from 'react';
import { Layout, theme, FloatButton, Drawer, Button, Space } from 'antd';
import {
  PlusOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  MenuOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import './DashboardLayout.css';

const { Content, Footer } = Layout;

const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Close mobile sidebar when route changes
    setMobileOpen(false);
  }, [location]);

  const handleCollapse = (collapsed) => {
    setCollapsed(collapsed);
  };

  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  const handleMobileToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const quickActions = [
    {
      icon: <PlusOutlined />,
      label: 'New Work Log',
      onClick: () => navigate('/operations'),
      color: '#1890ff',
      description: 'Record equipment operation'
    },
    {
      icon: <MessageOutlined />,
      label: 'Report Issue',
      onClick: () => {
        // Open issue reporting modal or page
        console.log('Report issue');
        // You could add: navigate('/operations?report=issue')
      },
      color: '#52c41a',
      description: 'Report safety or equipment issues'
    },
    {
      icon: <QuestionCircleOutlined />,
      label: 'Get Help',
      onClick: () => setShowHelp(true),
      color: '#722ed1',
      description: 'System documentation and support'
    }
  ];

  const getContentBackground = () => {
    const route = location.pathname.split('/')[1];
    const lightRoutes = ['dashboard', 'planning', 'costing'];
    return lightRoutes.includes(route) ? colorBgContainer : '#f0f2f5';
  };

  const renderHelpContent = () => (
    <div style={{ padding: 24 }}>
      <h3>Mine Planning System Help</h3>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <h4>Quick Start Guide</h4>
          <p>1. Use the sidebar to navigate between different modules</p>
          <p>2. Create mining blocks in the Planning section</p>
          <p>3. Record daily operations in the Operations section</p>
          <p>4. Monitor costs and progress in the Cost Analysis section</p>
          <p>5. Manage equipment in the Equipment section</p>
        </div>
        
        <div>
          <h4>Contact Support</h4>
          <p>Email: support@mineplan.com</p>
          <p>Phone: +27 11 123 4567</p>
          <p>Emergency: +27 11 999 9999</p>
        </div>
        
        <div>
          <h4>System Status</h4>
          <p>• All systems operational</p>
          <p>• Last updated: Today, 08:00 AM</p>
          <p>• Next maintenance: Saturday, 02:00 AM</p>
        </div>
      </Space>
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Mobile Menu Button */}
      <Button
        type="primary"
        icon={mobileOpen ? <CloseOutlined /> : <MenuOutlined />}
        onClick={handleMobileToggle}
        style={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1001,
          display: isMobile ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />

      <Sidebar
        collapsed={collapsed}
        onCollapse={handleCollapse}
        mobileOpen={mobileOpen}
        onMobileClose={handleMobileClose}
        isMobile={isMobile}
      />

      <Layout 
        style={{ 
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 256),
          transition: 'all 0.3s ease'
        }}
      >
        <Navbar
          collapsed={collapsed}
          onCollapse={handleCollapse}
          showSidebarToggle={!isMobile}
        />

        <Content
          style={{
            margin: isMobile ? '0' : '24px 16px',
            padding: isMobile ? '16px' : 24,
            background: getContentBackground(),
            borderRadius: borderRadiusLG,
            minHeight: 280,
            overflow: 'initial'
          }}
        >
          <div style={{ 
            background: getContentBackground(),
            borderRadius: borderRadiusLG,
            height: '100%'
          }}>
            <Outlet />
          </div>
        </Content>

        <Footer
          style={{
            textAlign: 'center',
            padding: '16px 24px',
            background: colorBgContainer,
            borderTop: '1px solid #f0f0f0'
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 16
          }}>
            <div>
              MinePlan Pro © {new Date().getFullYear()} - Coal Mining Operations Management System
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <Button type="link" href="/privacy" size="small">Privacy Policy</Button>
              <Button type="link" href="/terms" size="small">Terms of Service</Button>
              <Button type="link" onClick={() => setShowHelp(true)} size="small">Help Center</Button>
            </div>
          </div>
          <div style={{ 
            marginTop: 8, 
            fontSize: 12, 
            color: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8
          }}>
            <div>
              User: {user?.username || 'Guest'} | 
              Role: {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Viewer'} | 
              Last Login: Today, 08:30 AM
            </div>
            <div>
              System Version: 2.4.1 | 
              Database: Connected ✓ | 
              API: Operational ✓
            </div>
          </div>
        </Footer>
      </Layout>

      {/* Quick Action Float Button */}
      <FloatButton.Group
        shape="square"
        style={{ right: 24, bottom: 24 }}
        icon={<PlusOutlined />}
        trigger="hover"
      >
        {quickActions.map((action, index) => (
          <FloatButton
            key={index}
            icon={action.icon}
            onClick={action.onClick}
            tooltip={(
              <div style={{ 
                whiteSpace: 'nowrap',
                fontSize: 12,
                lineHeight: 1.5
              }}>
                <div style={{ fontWeight: 'bold' }}>{action.label}</div>
                <div style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {action.description}
                </div>
              </div>
            )}
            style={{ 
              backgroundColor: action.color,
              borderColor: action.color
            }}
          />
        ))}
      </FloatButton.Group>

      {/* Help Drawer */}
      <Drawer
        title="Help & Support"
        placement="right"
        onClose={() => setShowHelp(false)}
        open={showHelp}
        width={Math.min(400, window.innerWidth - 32)}
      >
        {renderHelpContent()}
      </Drawer>
    </Layout>
  );
};

export default DashboardLayout;